import { Button, Input, Select } from "@threadws/jute";
import { useState } from "react";
import type { Persona, Task, UpdateTask } from "../../api/generated";
import { formatEstimate, parseEstimate } from "../../utils/taskLabels";
import { RepeatSelect } from "./RepeatSelect";

const NO_PERSONA = "";
const PRIORITIES = [
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];
const fieldClass =
  "w-full rounded-md border border-border-subtle bg-transparent px-2.5 py-1.5 font-body text-sm text-text-primary focus:border-border-strong focus:outline-none";

interface TaskEditorProps {
  task: Task;
  personas: Persona[];
  /** Called with just the changed field, as soon as it changes (text fields on blur). */
  onSave: (changes: UpdateTask) => void;
  onDelete: () => void;
  deleting: boolean;
}

/** Every field of a task, each saved as you change it. */
export function TaskEditor({ task, personas, onSave, onDelete, deleting }: TaskEditorProps) {
  const [title, setTitle] = useState(task.title);
  const [estimate, setEstimate] = useState(task.estimateMinutes === null ? "" : formatEstimate(task.estimateMinutes));
  const estimateMinutes = parseEstimate(estimate);
  const estimateInvalid = estimate.trim() !== "" && estimateMinutes === null;

  const saveTitle = () => {
    if (title.trim() && title.trim() !== task.title) onSave({ title });
    else setTitle(task.title);
  };
  const saveEstimate = () => {
    if (!estimateInvalid && estimateMinutes !== task.estimateMinutes) onSave({ estimateMinutes });
  };

  return (
    <div className="grid grid-cols-1 gap-3 rounded-md bg-surface-card p-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} onBlur={saveTitle} />
      </div>
      <Select
        label="Persona"
        value={task.personaId ?? NO_PERSONA}
        options={[{ value: NO_PERSONA, label: "No persona" }, ...personas.map(({ id, name }) => ({ value: id, label: name }))]}
        onChange={(event) => onSave({ personaId: event.target.value || null })}
      />
      <Select
        label="Priority"
        value={task.priority}
        options={PRIORITIES}
        onChange={(event) => onSave({ priority: event.target.value as Task["priority"] })}
      />
      <label className="flex flex-col gap-1.5 text-sm">
        Due
        <input
          type="date"
          className={fieldClass}
          value={task.dueOn ?? ""}
          onChange={(event) => onSave({ dueOn: event.target.value || null })}
        />
      </label>
      <Input
        label="Estimate"
        placeholder="e.g. 30m, 2h"
        value={estimate}
        error={estimateInvalid ? "Try 45m, 2h or 1h30" : undefined}
        onChange={(event) => setEstimate(event.target.value)}
        onBlur={saveEstimate}
      />
      <RepeatSelect value={task.recurrence} onChange={(recurrence) => onSave({ recurrence })} />
      <div className="flex justify-end sm:col-span-2">
        <Button variant="secondary" size="sm" icon="" disabled={deleting} onClick={onDelete}>
          {deleting ? "Deleting…" : "Delete task"}
        </Button>
      </div>
    </div>
  );
}
